import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPainPoints, getRecommendations, getBusinessImpacts, getOverallPainPointSummary, getPainPointDeepDive } from '../services/intelligence';

const MarkdownRenderer = ({ content }) => {
  const lines = content.split('\n');
  return (
    <div className="space-y-3">
      {lines.map((line, idx) => {
        if (line.startsWith('### ')) {
          return <h4 key={idx} className="text-md font-bold text-indigo-900 mt-4">{line.replace('### ', '')}</h4>;
        }
        if (line.startsWith('## ')) {
          return <h3 key={idx} className="text-lg font-bold text-indigo-900 mt-6 border-b border-indigo-100 pb-2">{line.replace('## ', '')}</h3>;
        }
        if (line.startsWith('# ')) {
          return <h2 key={idx} className="text-xl font-bold text-indigo-900 mt-6">{line.replace('# ', '')}</h2>;
        }
        if (line.startsWith('- ')) {
          const rawText = line.replace('- ', '');
          const parts = rawText.split('**');
          return <li key={idx} className="ml-4 list-disc text-gray-700">
            {parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="text-gray-900">{part}</strong> : part)}
          </li>;
        }
        if (line.trim() === '---') {
          return <hr key={idx} className="my-6 border-indigo-100 border-t-2" />;
        }
        if (line.trim() === '') return null;

        const parts = line.split('**');
        return <p key={idx} className="text-gray-700 leading-relaxed">
          {parts.map((part, i) => i % 2 === 1 ? <strong key={i} className="text-gray-900">{part}</strong> : part)}
        </p>;
      })}
    </div>
  );
};

const IntelligenceCard = ({ item, type, analysisId }) => {
  const [deepDive, setDeepDive] = useState(null);
  const [loadingDeepDive, setLoadingDeepDive] = useState(false);

  const handleDeepDive = async () => {
    if (deepDive) return;
    setLoadingDeepDive(true);
    try {
      const res = await getPainPointDeepDive(analysisId, item._id || item.id);
      setDeepDive(res.report);
    } catch (e) {
      alert("Failed to generate deep dive.");
    } finally {
      setLoadingDeepDive(false);
    }
  };
  const severityColors = {
    low: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    high: 'bg-orange-100 text-orange-800',
    critical: 'bg-red-100 text-red-800',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {item.category} • {item.department} • {(item.confidence * 100).toFixed(0)}% Confidence
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${severityColors[item.severity] || severityColors.medium}`}>
          {item.severity}
        </span>
      </div>

      <div className="space-y-4">
        {type === 'pain_point' && (
          <>
            <div>
              <span className="font-semibold text-gray-700 block">Observation:</span>
              <p className="text-gray-600 mt-1">{item.observation}</p>
            </div>
            <div>
              <span className="font-semibold text-gray-700 block">Root Cause:</span>
              <p className="text-gray-600 mt-1">{item.root_cause}</p>
            </div>
          </>
        )}

        {type === 'impact' && (
          <>
            <div>
              <span className="font-semibold text-gray-700 block">Business Impact:</span>
              <p className="text-gray-600 mt-1">{item.business_impact}</p>
            </div>
            <div>
              <span className="font-semibold text-gray-700 block">Estimated Risk:</span>
              <p className="text-gray-600 mt-1">{item.estimated_risk}</p>
            </div>
          </>
        )}

        {type === 'recommendation' && (
          <>
            <div>
              <span className="font-semibold text-gray-700 block">Recommended Solution:</span>
              <p className="text-gray-600 mt-1">{item.recommended_solution}</p>
            </div>
            <div className="flex gap-4 text-sm text-gray-500 mt-2 bg-gray-50 p-2 rounded">
              <span>Priority: {item.priority}/5</span>
              <span>Complexity: {item.implementation_complexity}</span>
              <span>ROI: {item.expected_roi}</span>
            </div>
          </>
        )}

        {item.evidence && item.evidence.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">Evidence Used</span>
            <ul className="text-sm text-gray-600 space-y-1 list-disc pl-5">
              {item.evidence.map((e, idx) => (
                <li key={idx}>
                  <span className="font-medium text-gray-800">{e.collector}</span>: {e.detail} ({e.source})
                </li>
              ))}
            </ul>
          </div>
        )}

        {type === 'pain_point' && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={handleDeepDive}
              disabled={loadingDeepDive}
              className={`px-4 py-2 rounded-lg font-medium text-sm text-white transition-colors ${loadingDeepDive ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
            >
              {loadingDeepDive ? 'Analyzing...' : 'Generate Deep Dive AI Report'}
            </button>

            {deepDive && (
              <div className="mt-6 p-6 bg-indigo-50 border border-indigo-100 rounded-xl">
                <MarkdownRenderer content={deepDive} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const BusinessIntelligence = () => {
  const { id } = useParams();
  const [painPoints, setPainPoints] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [impacts, setImpacts] = useState([]);
  const [overallSummary, setOverallSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pain_points');

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      try {
        const [pp, recs, imp] = await Promise.all([
          getPainPoints(id),
          getRecommendations(id),
          getBusinessImpacts(id)
        ]);
        setPainPoints(pp);
        setRecommendations(recs);
        setImpacts(imp);
      } catch (error) {
        console.error("Failed to fetch intelligence:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) {
    return <div className="p-8 flex justify-center"><div className="animate-pulse">Loading AI Analysis...</div></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-2">Business Intelligence Report</h1>
      <p className="text-gray-500 mb-8">AI-generated insights based on collected evidence.</p>

      <div className="flex space-x-4 mb-6 border-b">
        <button
          className={`pb-2 px-1 ${activeTab === 'pain_points' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('pain_points')}
        >
          Pain Points ({painPoints.length})
        </button>
        <button
          className={`pb-2 px-1 ${activeTab === 'impacts' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('impacts')}
        >
          Business Impact ({impacts.length})
        </button>
        <button
          className={`pb-2 px-1 ${activeTab === 'recommendations' ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500 hover:text-gray-700'}`}
          onClick={() => setActiveTab('recommendations')}
        >
          Recommendations ({recommendations.length})
        </button>
      </div>

      {activeTab === 'pain_points' && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-900">Executive AI Summary</h2>
            {!overallSummary && painPoints.length > 0 && (
              <button
                onClick={async () => {
                  setLoadingSummary(true);
                  try {
                    const res = await getOverallPainPointSummary(id);
                    setOverallSummary(res.summary);
                  } catch (e) {
                    alert("Failed to generate summary");
                  }
                  setLoadingSummary(false);
                }}
                disabled={loadingSummary}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
              >
                {loadingSummary ? 'Generating...' : 'Generate Overall Summary'}
              </button>
            )}
          </div>

          {overallSummary && (
            <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl mb-6 shadow-sm">
              <MarkdownRenderer content={overallSummary} />
            </div>
          )}
        </div>
      )}

      <div>
        {activeTab === 'pain_points' && (
          painPoints.length === 0 ? <p className="text-gray-500 italic">No pain points detected yet.</p> :
          painPoints.map(item => <IntelligenceCard key={item._id || item.id} item={item} type="pain_point" analysisId={id} />)
        )}

        {activeTab === 'impacts' && (
          impacts.length === 0 ? <p className="text-gray-500 italic">No business impacts detected yet.</p> :
          impacts.map(item => <IntelligenceCard key={item._id || item.id} item={item} type="impact" analysisId={id} />)
        )}

        {activeTab === 'recommendations' && (
          recommendations.length === 0 ? <p className="text-gray-500 italic">No recommendations generated yet.</p> :
          recommendations.map(item => <IntelligenceCard key={item._id || item.id} item={item} type="recommendation" analysisId={id} />)
        )}
      </div>
    </div>
  );
};

export default BusinessIntelligence;
